import { computed, type ComputedRef, type Ref, unref } from "vue"
import {
  ApplyResponseCode,
  type PaymentPlanResponse,
  type ProviderApplyResponseView,
  type ProviderKey,
} from "@versatile-credit-apply/omni-apply-client"

import { useCurrentProvider } from "@versatile-credit-apply/composition"

import {
  sortBy,
  SortRules,
  useProviderStore,
  ProviderStore,
  Nullable,
  usePurchaseAmount,
} from "@versatile-credit-apply/sdk"

const defaultSortBy: SortRules<PaymentPlanResponse> = [
  "paymentAmountCents",
  "minAmountCents",
  "providerKey",
]

export interface UsePlanLauncherPlansOptions {
  sortBy?: SortRules<PaymentPlanResponse> | Ref<SortRules<PaymentPlanResponse>>
}

export interface PlanLauncherSummary {
  providerKey: ProviderKey
  allPlans: PaymentPlanResponse[]
  bestPlan: PaymentPlanResponse
  partialCoverage: boolean
  response: ProviderApplyResponseView
}

interface IndexedPlanLauncherSummary extends PlanLauncherSummary {
  index: number
}

export function usePlanLauncherSummaries(
  options?: Nullable<UsePlanLauncherPlansOptions>
): ComputedRef<PlanLauncherSummary[]> {
  const eligible = useIsApplicationResponseEligibleForPlanLauncher()
  const store = useProviderStore()
  const purchaseAmount = usePurchaseAmount()

  return computed(() => {
    const summaries: IndexedPlanLauncherSummary[] = store.preauthResponses
      .filter(eligible)
      .map((response: ProviderApplyResponseView, index: number) => {
        const plans = store.findPaymentPlans(response.providerKey)

        const sortedPlans = sortBy(
          plans.paymentPlans,
          unref(options?.sortBy) ?? defaultSortBy
        )

        if (!sortedPlans.length) {
          return null
        }

        const summary: IndexedPlanLauncherSummary = {
          providerKey: response.providerKey,
          bestPlan: sortedPlans[0]!,
          allPlans: sortedPlans,
          index,
          partialCoverage:
            (response.approvedAmountCents ?? 0) < (purchaseAmount.value ?? 0),
          response,
        }

        return summary
      })
      .filter((plan) => plan !== null)

    return sortBy(summaries, ["partialCoverage", "index"]) ?? []
  })
}

export function hasMultiplePlanLauncherProviders(
  store: ProviderStore
): boolean {
  return (
    store.preauthResponses.filter((response: ProviderApplyResponseView) => {
      return (
        isApplicationResponseEligibleForPlanLauncher(store, response) &&
        store.findPaymentPlans(response.providerKey).paymentPlans.length >= 1
      )
    }).length > 1
  )
}

function isApplicationResponseEligibleForPlanLauncher(
  store: ProviderStore,
  response?: ProviderApplyResponseView,
  currentProvider?: Nullable<ProviderKey>
): boolean {
  let actualResponse: Nullable<ProviderApplyResponseView> = null

  currentProvider ??= store.currentProviderKey

  if (response == null) {
    return false
  } else if (response.providerKey) {
    actualResponse = store.findPreauthResponse(response.providerKey)
  }

  if (!actualResponse) {
    return false
  }

  if (
    !ApplyResponseCode.isAnyPreApproval(actualResponse.responseCode) &&
    actualResponse.responseCode !== ApplyResponseCode.PENDING
  ) {
    return false
  }

  if (actualResponse.providerKey === currentProvider) {
    return true
  }

  const applyResponse = store.findApplyResponse(actualResponse.providerKey)

  if (applyResponse) {
    return false
  }

  const responseConfig = store.findProviderConfig(actualResponse.providerKey)

  if (!responseConfig) {
    return false
  }

  const currentConfig = store.currentProviderConfig

  return responseConfig.cascadeGroup === currentConfig?.cascadeGroup
}

function useIsApplicationResponseEligibleForPlanLauncher(): (
  response: ProviderApplyResponseView
) => boolean {
  const currentProvider = useCurrentProvider()
  const providerStore = useProviderStore()

  return (response: ProviderApplyResponseView): boolean =>
    isApplicationResponseEligibleForPlanLauncher(
      providerStore,
      response,
      currentProvider.value
    )
}
